import React, { useEffect, useRef, useState } from "react";
import { ActionIcon, Box, Text } from "@mantine/core";
import { IconArrowDown, IconArrowUp, IconX } from "@tabler/icons-react";
import Pill from "../../../common/Pill";
import { moveBy } from "./customizeRules";
import { SortableListProps } from "./SortableList";
import styles from "./CustomizeDrawer.module.css";

type Arrow = "up" | "down";

/**
 * The phone version of SortableList (same props): a numbered list reordered
 * with up / down arrow buttons instead of dragging, with remove buttons.
 * When a move takes an item to the end of the list, the arrow just pressed
 * becomes disabled, so focus moves to that row's other arrow.
 */
export default function ArrowReorderList({
  items,
  ariaLabel,
  onReorder,
  onRemove,
  canRemove,
  firstNumber = 1,
}: SortableListProps) {
  const ids = items.map((i) => i.id);
  const arrows = useRef(new Map<string, HTMLButtonElement | null>());
  const [refocus, setRefocus] = useState<{ id: string; arrow: Arrow } | null>(
    null,
  );

  // After the reordered list has rendered.
  useEffect(() => {
    if (!refocus) return;
    arrows.current.get(`${refocus.id}:${refocus.arrow}`)?.focus();
    setRefocus(null);
  }, [refocus]);

  const move = (id: string, delta: -1 | 1) => {
    const next = moveBy(ids, id, delta);
    if (next === ids) return;
    onReorder(next);
    const at = next.indexOf(id);
    if (delta === -1 && at === 0) setRefocus({ id, arrow: "down" });
    if (delta === 1 && at === next.length - 1) setRefocus({ id, arrow: "up" });
  };
  const arrowRef =
    (id: string, arrow: Arrow) => (el: HTMLButtonElement | null) => {
      arrows.current.set(`${id}:${arrow}`, el);
    };

  return (
    <Box role="list" aria-label={ariaLabel} className={styles.selectedList}>
      {items.map((item, i) => (
        <Box role="listitem" key={item.id}>
          <Box className={styles.selectedRow}>
            <Pill tone="tint">{firstNumber + i}</Pill>
            <Text component="span" className={styles.rowLabel}>
              {item.label}
            </Text>
            <ActionIcon
              ref={arrowRef(item.id, "up")}
              variant="subtle"
              color="gray"
              size="lg"
              aria-label={`Move ${item.label} up`}
              disabled={i === 0}
              onClick={() => move(item.id, -1)}
            >
              <IconArrowUp size={16} aria-hidden />
            </ActionIcon>
            <ActionIcon
              ref={arrowRef(item.id, "down")}
              variant="subtle"
              color="gray"
              size="lg"
              aria-label={`Move ${item.label} down`}
              disabled={i === items.length - 1}
              onClick={() => move(item.id, 1)}
            >
              <IconArrowDown size={16} aria-hidden />
            </ActionIcon>
            {canRemove && (
              <ActionIcon
                variant="subtle"
                color="gray"
                size="lg"
                aria-label={`Remove ${item.label}`}
                onClick={() => onRemove(item.id)}
              >
                <IconX size={15} aria-hidden />
              </ActionIcon>
            )}
          </Box>
        </Box>
      ))}
    </Box>
  );
}
